import type {
  AdminAsset,
  AdminPageBlock,
  JournalPostSummary,
  PageSummary,
  PricingPlanItem,
  RichText,
  ShootSummary,
  VersionedPage,
  VersionedPricing,
} from '@diana/contracts';
import { ExternalLink, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { CompactRichTextField } from '../../components/CompactRichTextField.js';
import {
  WeddingHeroImageField,
  type WeddingHeroImagePreview,
} from '../media/WeddingHeroImageField.js';
import { WeddingHeroPreview } from './WeddingHeroPreview.js';
import { weddingBookingYears } from '../../../../site/src/scripts/wedding-booking-years.js';
import { plainToRichText, richTextToPlain } from '../../components/RichTextField.js';
import { Button, Checkbox } from '../../ui/index.js';
import { ContextualGalleryField, ContextualMediaField } from '../media/ContextualMediaField.js';
import { SelectField, TextAreaField, TextField } from '../settings/SettingsEditor.js';
import './HomepageEditor.css';
import {
  EditorFieldGroup,
  EditorCollectionItem,
  EditorCollectionActions,
} from './PageEditorFields.js';
import { PageSection, PageSectionsWorkspace } from './PageSectionsWorkspace.js';

import { GiftHeroFields, GiftInquiryFields, GiftSessionsFields } from './GiftCardFields.js';

type HeroBlock = Extract<AdminPageBlock, { type: 'Hero' }>;
type EditorialBlock = Extract<AdminPageBlock, { type: 'EditorialText' }>;
type GalleryBlock = Extract<AdminPageBlock, { type: 'Gallery' }>;
type PricingBlock = Extract<AdminPageBlock, { type: 'Pricing' }>;
type ProcessBlock = Extract<AdminPageBlock, { type: 'Process' }>;
type FaqBlock = Extract<AdminPageBlock, { type: 'FAQ' }>;
type CtaBlock = Extract<AdminPageBlock, { type: 'CTA' }>;

export type PageDraftPatch = Partial<Pick<VersionedPage, 'payload' | 'siteAccess'>>;

export function StructuredPageEditor({
  page,
  blocks,
  assets,
  shoots,
  journal,
  pricing,
  onBlockChange,
  onBlocksChange,
}: {
  page: VersionedPage;
  blocks: readonly AdminPageBlock[];
  assets: readonly AdminAsset[];
  shoots: readonly ShootSummary[];
  journal: readonly JournalPostSummary[];
  pricing?: VersionedPricing;
  onBlockChange(block: AdminPageBlock): void;
  onBlocksChange(blocks: readonly AdminPageBlock[]): void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const hero = blockOfType(blocks, 'Hero');
  const editorial = blockOfType(blocks, 'EditorialText');
  const gallery = blockOfType(blocks, 'Gallery');
  const giftSessions = blockOfType(blocks, 'Feature');
  const pricingBlock = blockOfType(blocks, 'Pricing');
  const process = blockOfType(blocks, 'Process');
  const faq = blockOfType(blocks, 'FAQ');
  const cta = blockOfType(blocks, 'CTA');
  let weddingSectionIndex = 0;
  const section = (
    id: string,
    index: string,
    title: string,
    description: string,
    content: ReactNode,
    source = 'Editable here',
  ) => (
    <FixedSection
      key={id}
      id={id}
      index={
        page.kind === 'WeddingsService' ? String(++weddingSectionIndex).padStart(2, '0') : index
      }
      title={title}
      description={description}
      expanded={expanded === id}
      source={source}
      onToggle={() => setExpanded(expanded === id ? null : id)}
    >
      {content}
    </FixedSection>
  );

  const content =
    page.kind === 'About'
      ? [
          section(
            'hero',
            '01',
            'Hero',
            'One semantic H1 with a visual accent and a short introduction.',
            hero ? (
              <HeroFields block={hero} assets={assets} onChange={onBlockChange} />
            ) : (
              <MissingSection />
            ),
            'Editable here',
          ),
          section(
            'biography',
            '02',
            'Biography',
            'Portrait, biography paragraphs and signature.',
            editorial ? (
              <BiographyFields block={editorial} assets={assets} onChange={onBlockChange} />
            ) : (
              <MissingSection />
            ),
            'Editable here',
          ),
          section(
            'facts',
            '03',
            'Facts',
            'A bounded list of short facts about Diana and her work.',
            editorial ? <FactsFields block={editorial} onChange={onBlockChange} /> : null,
          ),
          section(
            'work',
            '04',
            'How I work',
            'One image, one statement and one supporting note.',
            editorial ? (
              <FeatureFields block={editorial} assets={assets} onChange={onBlockChange} />
            ) : null,
          ),
          section(
            'press',
            '05',
            'Press',
            'Publications, dates and optional external links.',
            editorial ? (
              <IndexFields block={editorial} collection="press" onChange={onBlockChange} />
            ) : null,
          ),
          section(
            'education',
            '06',
            'Education',
            'Courses, workshops and photography education.',
            editorial ? (
              <IndexFields block={editorial} collection="education" onChange={onBlockChange} />
            ) : null,
          ),
          section(
            'contact',
            '07',
            'Contact CTA',
            'Closing message with a protected destination.',
            cta ? <CtaFields block={cta} onChange={onBlockChange} /> : <MissingSection />,
            'Editable here',
          ),
        ]
      : page.kind === 'PortfolioIndex'
        ? [
            section(
              'hero',
              '01',
              'Portfolio heading',
              'The page title remains one semantic H1.',
              hero ? (
                <HeroFields block={hero} assets={assets} onChange={onBlockChange} compact />
              ) : (
                <MissingSection />
              ),
            ),
            section(
              'categories',
              '02',
              'Portfolio categories',
              'Titles, homepage visibility and SEO for each category.',
              gallery ? (
                <PortfolioCategories block={gallery} onChange={onBlockChange} />
              ) : (
                <MissingSection />
              ),
            ),
            section(
              'shoots',
              '03',
              'Portfolio galleries',
              'Public galleries are assigned to the categories above.',
              <ShootSource shoots={shoots} />,
              'Linked content',
            ),
          ]
        : page.kind === 'PricingPage'
          ? [
              section(
                'hero',
                '01',
                'Page introduction',
                'Title, introduction and the compact list of included benefits.',
                hero ? (
                  <HeroFields block={hero} assets={assets} onChange={onBlockChange} />
                ) : (
                  <MissingSection />
                ),
              ),
              section(
                'packages',
                '02',
                'Packages',
                'One shared catalog used by Pricing, Homepage and service pages.',
                <PackageSource pricing={pricing} />,
                'Linked content',
              ),
              section(
                'extras',
                '03',
                'Extras',
                'Optional additions and their public prices.',
                pricingBlock ? (
                  <PricingExtrasFields block={pricingBlock} onChange={onBlockChange} />
                ) : (
                  <MissingSection />
                ),
              ),
              section(
                'process',
                '04',
                'Booking process',
                'Steps shown only on the Pricing page.',
                process ? (
                  <ProcessFields block={process} onChange={onBlockChange} showEditorialFields />
                ) : (
                  <MissingSection />
                ),
              ),
              section(
                'faq',
                '05',
                'FAQ',
                'Questions and answers shown on the Pricing page.',
                faq ? <FaqFields block={faq} onChange={onBlockChange} /> : <MissingSection />,
              ),
              section(
                'gift',
                '06',
                'Gift card',
                'Gift offer shown after the packages and extras.',
                pricingBlock ? (
                  <PricingGiftFields block={pricingBlock} onChange={onBlockChange} />
                ) : (
                  <MissingSection />
                ),
              ),
            ]
          : page.kind === 'JournalIndex'
            ? [
                section(
                  'hero',
                  '01',
                  'Journal heading',
                  'The page title, visual accent and introduction.',
                  hero ? (
                    <HeroFields block={hero} assets={assets} onChange={onBlockChange} />
                  ) : (
                    <MissingSection />
                  ),
                ),
                section(
                  'categories',
                  '02',
                  'Categories',
                  'Automatically collected from existing articles to prevent empty filters.',
                  <JournalCategories journal={journal} />,
                  'Automatic',
                ),
                section(
                  'featured',
                  '03',
                  'Featured article',
                  'Automatically selects the newest public article by publication date.',
                  <JournalFeatured journal={journal} />,
                  'Automatic',
                ),
                section(
                  'feed',
                  '04',
                  'Article feed',
                  'All remaining published articles are ordered by publication date.',
                  <LinkedSection
                    title={`${journal.length} article${journal.length === 1 ? '' : 's'} in Journal`}
                    body="Create and edit article content in the dedicated Journal workspace."
                    href="/journal"
                    action="Open Journal"
                  />,
                  'Linked content',
                ),
              ]
            : page.kind === 'GiftMarketing'
              ? [
                  section(
                    'hero',
                    '01',
                    'Hero and Gift card',
                    'First screen, gift card preview and its 4:5 photograph.',
                    hero ? (
                      <>
                        <GiftHeroFields block={hero} assets={assets} onChange={onBlockChange} />
                        {!giftSessions && hero.payload.feature && (
                          <HeroFeatureFields
                            block={hero}
                            assets={assets}
                            onChange={onBlockChange}
                          />
                        )}
                      </>
                    ) : (
                      <MissingSection />
                    ),
                  ),
                  section(
                    'sessions',
                    '02',
                    'Sessions',
                    'Three photography gifts with portrait 4:5 images.',
                    giftSessions ? (
                      <GiftSessionsFields
                        block={giftSessions}
                        assets={assets}
                        onChange={onBlockChange}
                      />
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => {
                          const position = blocks.findIndex((block) => block.type === 'Hero') + 1;
                          const next = [...blocks];
                          next.splice(position, 0, {
                            id: crypto.randomUUID(),
                            type: 'Feature',
                            enabled: true,
                            position,
                            payload: {
                              variant: 'Collection',
                              heading: 'A photoshoot to suit them',
                              body: plainToRichText(
                                'A birthday, an anniversary, or simply a reason to make someone feel special.',
                              ),
                              items: [
                                {
                                  key: crypto.randomUUID(),
                                  title: 'Just for them',
                                  body: plainToRichText(
                                    'A portrait session with space to relax and be themselves.',
                                  ),
                                },
                              ],
                              action: {
                                label: 'Explore the sessions',
                                href: '/pricing',
                                style: 'Secondary',
                              },
                            },
                          });
                          onBlocksChange(next.map((block, position) => ({ ...block, position })));
                        }}
                      >
                        Add sessions section
                      </Button>
                    ),
                  ),
                  section(
                    'process',
                    '03',
                    'How it works',
                    'Choosing, giving and booking the gift.',
                    process ? (
                      <>
                        <ProcessFields
                          block={process}
                          showEditorialFields
                          showActionLabel={false}
                          onChange={onBlockChange}
                        />
                        {process.payload.details && process.payload.details.length > 0 && (
                          <ProcessDetailsFields block={process} onChange={onBlockChange} />
                        )}
                      </>
                    ) : (
                      <MissingSection />
                    ),
                  ),
                  section(
                    'faq',
                    '04',
                    'FAQ',
                    'Practical questions about giving a photoshoot.',
                    faq ? (
                      <FaqFields block={faq} onChange={onBlockChange} headingOnly />
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => {
                          const position = blocks.findIndex((block) => block.type === 'CTA');
                          const next = [...blocks];
                          next.splice(position < 0 ? next.length : position, 0, {
                            id: crypto.randomUUID(),
                            type: 'FAQ',
                            enabled: true,
                            position: 0,
                            payload: {
                              source: 'Inline',
                              heading: 'Gift card questions, answered',
                              items: [
                                {
                                  key: crypto.randomUUID(),
                                  question: 'Do I need to choose a date now?',
                                  answer: plainToRichText(
                                    'The recipient gets in touch, and we plan the session together.',
                                  ),
                                },
                              ],
                            },
                          });
                          onBlocksChange(next.map((block, position) => ({ ...block, position })));
                        }}
                      >
                        Add FAQ section
                      </Button>
                    ),
                  ),
                  section(
                    'contact',
                    '05',
                    'Final CTA',
                    'Closing invitation to enquire about a gift card.',
                    cta ? <CtaFields block={cta} onChange={onBlockChange} /> : <MissingSection />,
                  ),
                  section(
                    'inquiry',
                    '06',
                    'Enquiry form',
                    'Gift-specific form copy. Contact fields use the shared form.',
                    hero ? (
                      <GiftInquiryFields block={hero} onChange={onBlockChange} />
                    ) : (
                      <MissingSection />
                    ),
                  ),
                ]
              : page.kind === 'ProposalsService'
                ? [
                    section(
                      'hero',
                      '01',
                      'Hero',
                      'A single H1 plus service, location and price metadata.',
                      hero ? (
                        <HeroFields block={hero} assets={assets} onChange={onBlockChange} meta />
                      ) : (
                        <MissingSection />
                      ),
                    ),
                    section(
                      'about',
                      '02',
                      'About the service',
                      'One photograph, main statement and supporting note.',
                      editorial ? (
                        <FeatureFields block={editorial} assets={assets} onChange={onBlockChange} />
                      ) : (
                        <MissingSection />
                      ),
                    ),
                    section(
                      'process',
                      '03',
                      'How it works',
                      'Page-specific steps; starts from the shared process until first edited.',
                      process ? (
                        <ProcessFields block={process} pricing={pricing} onChange={onBlockChange} />
                      ) : (
                        <MissingSection />
                      ),
                    ),
                    section(
                      'packages',
                      '04',
                      'Proposal packages',
                      'Automatically uses every visible Proposals package from Pricing.',
                      <PackageSource pricing={pricing} service="Proposals" />,
                      'Linked content',
                    ),
                    section(
                      'contact',
                      '05',
                      'Contact CTA',
                      'Closing message with a protected destination.',
                      cta ? <CtaFields block={cta} onChange={onBlockChange} /> : <MissingSection />,
                    ),
                  ]
                : [
                    section(
                      'hero',
                      '01',
                      'Hero',
                      'One heading, description and primary photograph.',
                      hero ? (
                        <WeddingHeroFields
                          block={hero}
                          assets={assets}
                          onChange={onBlockChange}
                          buttonLabel={cta?.payload.action.label ?? 'Check your date'}
                        />
                      ) : (
                        <MissingSection />
                      ),
                    ),
                    section(
                      'gallery',
                      '02',
                      'Wedding gallery',
                      'Choose and order images; the public layout controls wide positions.',
                      editorial ? (
                        <ServiceGalleryFields
                          block={editorial}
                          assets={assets}
                          onChange={onBlockChange}
                        />
                      ) : (
                        <MissingSection />
                      ),
                    ),
                    section(
                      'facts',
                      '03',
                      'At a glance',
                      'The practical details couples look for first.',
                      editorial ? (
                        <FactsFields
                          block={editorial}
                          onChange={onBlockChange}
                          showHeadingAfter={false}
                        />
                      ) : null,
                    ),
                    section(
                      'photographer',
                      '04',
                      'Your photographer',
                      'Portrait, heading and two short paragraphs.',
                      editorial ? (
                        <BiographyFields
                          block={editorial}
                          assets={assets}
                          onChange={onBlockChange}
                          showEyebrow
                          showSignature={false}
                        />
                      ) : null,
                    ),
                    section(
                      'journey',
                      '05',
                      'Wedding journey',
                      'Three editorial chapters with one description each.',
                      process ? (
                        <JourneyFields block={process} onChange={onBlockChange} />
                      ) : (
                        <MissingSection />
                      ),
                    ),
                    section(
                      'packages',
                      '06',
                      'Wedding packages',
                      'Public heading may say Collections; data comes from shared Packages.',
                      <PackageSource pricing={pricing} service="Weddings" />,
                      'Linked content',
                    ),
                    process?.enabled &&
                    process.payload.reviews?.some((review) => review.approvedForWebsite === true)
                      ? section(
                          'reviews',
                          '07',
                          'Reviews',
                          'Published client quotes and attributions.',
                          <ReviewFields block={process} onChange={onBlockChange} />,
                        )
                      : null,
                    section(
                      'statement',
                      '08',
                      'Closing statement',
                      'One editorial statement with a visual accent.',
                      process ? <StatementFields block={process} onChange={onBlockChange} /> : null,
                    ),
                    faq?.enabled &&
                    (faq.payload.source === 'Pricing' || faq.payload.items.length > 0)
                      ? section(
                          'faq',
                          '09',
                          'Practical FAQ',
                          'Wedding-specific answers.',
                          <FaqFields block={faq} onChange={onBlockChange} />,
                        )
                      : null,
                    section(
                      'contact',
                      '10',
                      'Contact CTA',
                      'Closing message with a protected destination.',
                      cta ? <CtaFields block={cta} onChange={onBlockChange} /> : <MissingSection />,
                    ),
                  ];

  return <PageSectionsWorkspace>{content}</PageSectionsWorkspace>;
}

function FixedSection(props: {
  id: string;
  index: string;
  title: string;
  description: string;
  expanded: boolean;
  source: string;
  onToggle(): void;
  children: ReactNode;
}) {
  return <PageSection {...props} id={`structured-section-${props.id}`} />;
}

function WeddingHeroFields({
  block,
  assets,
  onChange,
  buttonLabel,
}: {
  block: HeroBlock;
  assets: readonly AdminAsset[];
  onChange(block: HeroBlock): void;
  buttonLabel: string;
}) {
  const payload = block.payload;
  const [imagePreview, setImagePreview] = useState<WeddingHeroImagePreview>();
  const composition = payload.imageComposition;
  const imageUrl = (assetId: string | undefined) =>
    assetId ? `/api/v1/admin/assets/${assetId}/preview` : undefined;
  const desktopUrl =
    imagePreview?.desktopUrl ??
    imageUrl(composition?.desktop.media.assetId ?? payload.media?.assetId);
  const mobileUrl =
    imagePreview?.mobileUrl ??
    imageUrl(composition?.mobile.media.assetId ?? payload.media?.assetId);
  const heading = payload.accent
    ? [payload.title.trim(), payload.accent.trim()].filter(Boolean).join(' ')
    : payload.title;
  const subtitle = payload.subtitle ?? plainToRichText('');
  const lead = payload.lead?.trim();
  const text = richTextToPlain(subtitle).trim();
  const [first, ...rest] = subtitle.children;
  const description: RichText =
    !lead || lead === text || (text.startsWith(lead) && /^\s/.test(text.slice(lead.length)))
      ? subtitle
      : !text
        ? plainToRichText(lead)
        : {
            type: 'document',
            children:
              first?.type === 'paragraph'
                ? [
                    { ...first, children: [{ type: 'text', text: `${lead} ` }, ...first.children] },
                    ...rest,
                  ]
                : [...plainToRichText(lead).children, ...subtitle.children],
          };
  const update = (patch: Partial<typeof payload>) =>
    onChange({
      ...block,
      payload: {
        ...payload,
        title: heading,
        subtitle: description,
        accent: undefined,
        eyebrow: undefined,
        lead: undefined,
        ...patch,
      },
    });

  return (
    <div className="home-fields">
      <TextField label="Heading" value={heading} onChange={(title) => update({ title })} />
      <CompactRichTextField
        label="Description"
        value={description}
        onChange={(subtitle) => update({ subtitle })}
      />
      <WeddingHeroImageField
        value={composition}
        legacyMedia={payload.media}
        assets={assets}
        onChange={(imageComposition) =>
          update({ imageComposition, media: imageComposition.desktop.media })
        }
        onPreviewChange={setImagePreview}
      />
      <WeddingHeroPreview
        heading={heading}
        description={description}
        buttonLabel={buttonLabel}
        bookingMessage={`Bookings are open for the ${weddingBookingYears(new Date()).replace(' and ', ' / ')} season!`}
        desktopImage={desktopUrl ? { url: desktopUrl, crop: imagePreview?.desktopCrop } : undefined}
        mobileImage={mobileUrl ? { url: mobileUrl, crop: imagePreview?.mobileCrop } : undefined}
        composed={!!composition || !!imagePreview}
      />
    </div>
  );
}

function HeroFields({
  block,
  assets,
  onChange,
  compact = false,
  meta = false,
}: {
  block: HeroBlock;
  assets: readonly AdminAsset[];
  onChange(block: HeroBlock): void;
  compact?: boolean;
  meta?: boolean;
}) {
  const payload = block.payload;
  const update = (patch: Partial<typeof payload>) =>
    onChange({ ...block, payload: { ...payload, ...patch } });
  return (
    <div className="home-fields">
      <EditorFieldGroup title="Page content">
        <div className="form-grid form-grid--two">
          <TextField
            label="Heading before accent"
            value={payload.title}
            onChange={(title) => update({ title })}
          />
          <TextField
            label="Italic text"
            value={payload.accent ?? ''}
            optional
            onChange={(accent) => update({ accent: accent || undefined })}
          />
        </div>
        {!compact ? (
          <>
            <TextField
              label="Eyebrow"
              value={payload.eyebrow ?? ''}
              optional
              onChange={(eyebrow) => update({ eyebrow: eyebrow || undefined })}
            />
            <CompactRichTextField
              label="Introduction"
              value={payload.subtitle ?? plainToRichText(payload.lead ?? '')}
              onChange={(subtitle) =>
                update({
                  lead: richTextToPlain(subtitle) || undefined,
                  subtitle,
                })
              }
            />
          </>
        ) : null}
        {meta ? (
          <div className="form-grid form-grid--three">
            {[0, 1, 2].map((index) => (
              <TextField
                key={index}
                label={`Meta ${index + 1}`}
                value={payload.meta?.[index] ?? ''}
                optional
                onChange={(value) => {
                  const next = [...(payload.meta ?? [])];
                  next[index] = value;
                  update({ meta: next.filter(Boolean) });
                }}
              />
            ))}
          </div>
        ) : null}
      </EditorFieldGroup>
      {payload.media ? (
        <EditorFieldGroup title="Photograph">
          <ContextualMediaField
            title="Hero image"
            profile="wide"
            cropProfile="landscape-3-2"
            description="Primary image for the top of this page."
            value={payload.media}
            assets={assets}
            onChange={(media) => update({ media })}
          />
        </EditorFieldGroup>
      ) : null}
    </div>
  );
}

function BiographyFields({
  block,
  assets,
  onChange,
  showEyebrow = false,
  showSignature = true,
}: {
  block: EditorialBlock;
  assets: readonly AdminAsset[];
  onChange(block: EditorialBlock): void;
  showEyebrow?: boolean;
  showSignature?: boolean;
}) {
  const payload = block.payload;
  const update = (patch: Partial<typeof payload>) =>
    onChange({ ...block, payload: { ...payload, ...patch } });
  return (
    <div className="home-fields">
      <EditorFieldGroup title="Biography">
        {showEyebrow ? (
          <TextField
            label="Eyebrow"
            value={payload.eyebrow ?? ''}
            optional
            onChange={(eyebrow) => update({ eyebrow: eyebrow || undefined })}
          />
        ) : null}
        <div className="form-grid form-grid--two">
          <TextField
            label="Heading before accent"
            value={payload.heading ?? ''}
            onChange={(heading) => update({ heading: heading || undefined })}
          />
          <TextField
            label="Italic text"
            value={payload.accent ?? ''}
            optional
            onChange={(accent) => update({ accent: accent || undefined })}
          />
        </div>
        <CompactRichTextField
          label="Biography text"
          value={biographyRichText(payload.body, payload.paragraphs, payload.heading)}
          onChange={(body) => update({ body, paragraphs: undefined })}
        />
        {showSignature ? (
          <TextField
            label="Signature"
            value={payload.signature ?? ''}
            optional
            onChange={(signature) => update({ signature: signature || undefined })}
          />
        ) : null}
      </EditorFieldGroup>
      {payload.media?.[0] ? (
        <EditorFieldGroup title="Photograph">
          <ContextualMediaField
            title="Portrait"
            profile="standard"
            cropProfile="portrait-4-5"
            description="Primary portrait for this biography section."
            value={payload.media[0]}
            assets={assets}
            onChange={(media) => update({ media: [media, ...(payload.media?.slice(1) ?? [])] })}
          />
        </EditorFieldGroup>
      ) : null}
    </div>
  );
}

function biographyRichText(
  body: RichText,
  paragraphs: string[] | undefined,
  emphasizedText: string | undefined,
): RichText {
  if (richTextHasMarks(body) || !paragraphs?.length) return body;
  const migrated = plainToRichText(paragraphs.join('\n\n'));
  if (!emphasizedText) return migrated;

  let marked = false;
  return {
    ...migrated,
    children: migrated.children.map((node) => {
      if (marked || node.type === 'bulletList') return node;
      const children = node.children.flatMap((child) => {
        if (marked || child.type === 'link') return [child];
        const index = child.text.indexOf(emphasizedText);
        if (index < 0) return [child];
        marked = true;
        return [
          ...(index > 0 ? [{ type: 'text' as const, text: child.text.slice(0, index) }] : []),
          {
            type: 'text' as const,
            text: emphasizedText,
            marks: ['strong' as const],
          },
          ...(index + emphasizedText.length < child.text.length
            ? [
                {
                  type: 'text' as const,
                  text: child.text.slice(index + emphasizedText.length),
                },
              ]
            : []),
        ];
      });
      return { ...node, children };
    }),
  };
}

function richTextHasMarks(value: RichText) {
  return value.children.some((node) =>
    node.type === 'bulletList'
      ? node.items.some((item) =>
          item.some((child) => child.type === 'text' && Boolean(child.marks?.length)),
        )
      : node.children.some((child) => child.type === 'text' && Boolean(child.marks?.length)),
  );
}

function FactsFields({
  block,
  onChange,
  showHeadingAfter = true,
}: {
  block: EditorialBlock;
  onChange(block: EditorialBlock): void;
  showHeadingAfter?: boolean;
}) {
  const facts = block.payload.facts;
  if (!facts) return <MissingSection />;
  const update = (next: typeof facts) =>
    onChange({ ...block, payload: { ...block.payload, facts: next } });
  return (
    <div className="home-fields">
      <TextField
        label="Eyebrow"
        value={facts.eyebrow ?? ''}
        onChange={(eyebrow) => update({ ...facts, eyebrow: eyebrow || undefined })}
      />
      <HeadingFields
        showAfter={showHeadingAfter}
        value={facts.heading}
        onChange={(heading) => update({ ...facts, heading })}
      />
      <div className="home-process-list">
        {facts.items.map((item, index) => (
          <article className="home-process-item" key={item.key}>
            <CollectionHeader
              label={`Fact ${index + 1}`}
              index={index}
              count={facts.items.length}
              onMove={(direction) =>
                update({ ...facts, items: moveItem(facts.items, index, direction) })
              }
              onRemove={() =>
                update({ ...facts, items: facts.items.filter((_, current) => current !== index) })
              }
            />
            <div className="form-grid form-grid--three">
              <TextField
                label="Label"
                value={item.label}
                onChange={(label) =>
                  update({ ...facts, items: replaceAt(facts.items, index, { ...item, label }) })
                }
              />
              <TextField
                label="Text"
                value={item.text}
                onChange={(text) =>
                  update({ ...facts, items: replaceAt(facts.items, index, { ...item, text }) })
                }
              />
              <TextField
                label="Italic text"
                value={item.accent ?? ''}
                optional
                onChange={(accent) =>
                  update({
                    ...facts,
                    items: replaceAt(facts.items, index, {
                      ...item,
                      accent: accent || undefined,
                    }),
                  })
                }
              />
            </div>
          </article>
        ))}
      </div>
      <Button
        variant="secondary"
        leadingIcon={Plus}
        disabled={facts.items.length >= 8}
        onClick={() =>
          update({
            ...facts,
            items: [
              ...facts.items,
              {
                key: crypto.randomUUID(),
                label: 'New fact',
                text: 'Add details',
              },
            ],
          })
        }
      >
        Add fact
      </Button>
    </div>
  );
}

function FeatureFields({
  block,
  assets,
  onChange,
}: {
  block: EditorialBlock;
  assets: readonly AdminAsset[];
  onChange(block: EditorialBlock): void;
}) {
  const feature = block.payload.feature;
  if (!feature) return <MissingSection />;
  const update = (next: typeof feature) =>
    onChange({ ...block, payload: { ...block.payload, feature: next } });
  return (
    <div className="home-fields">
      <EditorFieldGroup title="Photograph">
        <ContextualMediaField
          title="Feature image"
          profile="standard"
          cropProfile="portrait-4-5"
          description="Supporting photograph for this editorial feature."
          value={feature.media}
          assets={assets}
          onChange={(media) => update({ ...feature, media })}
        />
      </EditorFieldGroup>
      <EditorFieldGroup title="Feature text">
        <TextField
          label="Label"
          value={feature.label}
          onChange={(label) => update({ ...feature, label })}
        />
        <CompactRichTextField
          label="Main statement"
          value={feature.statementBody ?? plainToRichText(feature.statement)}
          onChange={(statementBody) =>
            update({
              ...feature,
              statement: richTextToPlain(statementBody),
              statementBody,
            })
          }
        />
        <CompactRichTextField
          label="Supporting note"
          value={feature.noteBody ?? plainToRichText(feature.note ?? '')}
          onChange={(noteBody) =>
            update({
              ...feature,
              note: richTextToPlain(noteBody) || undefined,
              noteBody,
            })
          }
        />
      </EditorFieldGroup>
    </div>
  );
}

function IndexFields({
  block,
  collection,
  onChange,
}: {
  block: EditorialBlock;
  collection: 'press' | 'education';
  onChange(block: EditorialBlock): void;
}) {
  const value = block.payload[collection];
  if (!value) return <MissingSection />;
  const update = (next: typeof value) =>
    onChange({ ...block, payload: { ...block.payload, [collection]: next } });
  return (
    <div className="home-fields">
      <HeadingFields value={value.heading} onChange={(heading) => update({ ...value, heading })} />
      <div className="home-process-list">
        {value.items.map((item, index) => (
          <article className="home-process-item" key={item.key}>
            <CollectionHeader
              label={`${collection === 'press' ? 'Publication' : 'Education'} ${index + 1}`}
              index={index}
              count={value.items.length}
              onMove={(direction) =>
                update({ ...value, items: moveItem(value.items, index, direction) })
              }
              onRemove={() =>
                update({ ...value, items: value.items.filter((_, current) => current !== index) })
              }
            />
            <div className="form-grid form-grid--two">
              <TextField
                label="Title"
                value={item.title}
                onChange={(title) =>
                  update({ ...value, items: replaceAt(value.items, index, { ...item, title }) })
                }
              />
              <TextField
                label="Italic text"
                value={item.accent ?? ''}
                optional
                onChange={(accent) =>
                  update({
                    ...value,
                    items: replaceAt(value.items, index, {
                      ...item,
                      accent: accent || undefined,
                    }),
                  })
                }
              />
              <TextField
                label="Meta"
                value={item.meta}
                onChange={(meta) =>
                  update({ ...value, items: replaceAt(value.items, index, { ...item, meta }) })
                }
              />
              <TextField
                label="Year"
                value={item.year ?? ''}
                optional
                onChange={(year) =>
                  update({
                    ...value,
                    items: replaceAt(value.items, index, { ...item, year: year || undefined }),
                  })
                }
              />
              {'href' in item ? (
                <TextField
                  label="External link"
                  value={(item as { href?: string }).href ?? ''}
                  optional
                  onChange={(href) => {
                    const next = { ...item } as typeof item & { href?: string };
                    next.href = href || undefined;
                    update({
                      ...value,
                      items: replaceAt(value.items, index, next),
                    });
                  }}
                />
              ) : null}
            </div>
          </article>
        ))}
      </div>
      <Button
        variant="secondary"
        leadingIcon={Plus}
        disabled={value.items.length >= 12}
        onClick={() =>
          update({
            ...value,
            items: [
              ...value.items,
              {
                key: crypto.randomUUID(),
                title: 'New entry',
                meta: collection === 'press' ? 'Editorial feature' : 'Photography',
              },
            ],
          })
        }
      >
        Add entry
      </Button>
    </div>
  );
}

function PortfolioCategories({
  block,
  onChange,
}: {
  block: GalleryBlock;
  onChange(block: GalleryBlock): void;
}) {
  const categories = block.payload.categories ?? [];
  const update = (next: typeof categories) =>
    onChange({ ...block, payload: { ...block.payload, categories: next } });
  return (
    <div className="home-fields">
      <div className="home-process-list">
        {categories.map((category, index) => (
          <article className="home-process-item" key={category.key}>
            <CollectionHeader
              label={category.title}
              index={index}
              count={categories.length}
              onMove={(direction) => update(moveItem(categories, index, direction))}
              onRemove={() => update(categories.filter((_, current) => current !== index))}
            />
            <div className="form-grid form-grid--two">
              <TextField
                label="Category title"
                value={category.title}
                onChange={(title) => update(replaceAt(categories, index, { ...category, title }))}
              />
              <TextField
                label="Slug"
                value={category.slug}
                onChange={(slug) => update(replaceAt(categories, index, { ...category, slug }))}
              />
            </div>
            <TextAreaField
              label="Category subtitle"
              value={category.subtitle}
              onChange={(subtitle) =>
                update(replaceAt(categories, index, { ...category, subtitle }))
              }
            />
            <div className="home-choice-list">
              <div className="home-choice">
                <Checkbox
                  label="Visible category"
                  description="Show this filter and its published shoots."
                  checked={category.visible}
                  onChange={(event) =>
                    update(
                      replaceAt(categories, index, {
                        ...category,
                        visible: event.currentTarget.checked,
                      }),
                    )
                  }
                />
              </div>
              <div className="home-choice">
                <Checkbox
                  label="Hide from Homepage"
                  description="Categories appear on Homepage by default."
                  checked={category.hideFromHome}
                  onChange={(event) =>
                    update(
                      replaceAt(categories, index, {
                        ...category,
                        hideFromHome: event.currentTarget.checked,
                      }),
                    )
                  }
                />
              </div>
            </div>
            <TextField
              label="SEO title"
              value={category.seo.title}
              onChange={(title) =>
                update(
                  replaceAt(categories, index, {
                    ...category,
                    seo: { ...category.seo, title },
                  }),
                )
              }
            />
            <TextAreaField
              label="SEO description"
              value={category.seo.description}
              onChange={(description) =>
                update(
                  replaceAt(categories, index, {
                    ...category,
                    seo: { ...category.seo, description },
                  }),
                )
              }
            />
          </article>
        ))}
      </div>
      <Button
        variant="secondary"
        leadingIcon={Plus}
        disabled={categories.length >= 12}
        onClick={() =>
          update([
            ...categories,
            {
              key: crypto.randomUUID(),
              slug: `category-${categories.length + 1}`,
              title: 'New category',
              subtitle: 'Photography stories',
              hideFromHome: false,
              visible: true,
              seo: {
                title: 'Photography category — Diana Perova',
                description: 'Photography stories by Diana Perova in Porto, Portugal.',
                noindex: false,
              },
            },
          ])
        }
      >
        Add category
      </Button>
    </div>
  );
}

function ShootSource({ shoots }: { shoots: readonly ShootSummary[] }) {
  return (
    <div className="home-fields">
      <div className="home-choice-list">
        {shoots.map((shoot) => (
          <div className="home-choice" key={shoot.id}>
            <span>
              <strong>{shoot.payload.title}</strong>
              <small>{shoot.payload.category ?? 'No category assigned'}</small>
            </span>
            <span className="entity-row__kind">
              {shoot.siteAccess === 'Public' ? 'Public' : 'Admin only'}
            </span>
          </div>
        ))}
      </div>
      <LinkedSection
        title="Assign categories inside each gallery"
        body="Portfolio cards are generated from public galleries."
        href="/shoots"
        action="Open Galleries"
      />
    </div>
  );
}

function HeroFeatureFields({
  block,
  assets,
  onChange,
}: {
  block: HeroBlock;
  assets: readonly AdminAsset[];
  onChange(block: HeroBlock): void;
}) {
  const feature = block.payload.feature;
  if (!feature) return <MissingSection />;
  const update = (next: typeof feature) =>
    onChange({ ...block, payload: { ...block.payload, feature: next } });
  return (
    <div className="home-fields">
      <ContextualMediaField
        title="Feature image"
        profile="standard"
        cropProfile="portrait-4-5"
        description="Supporting photograph for this hero feature."
        value={feature.media}
        assets={assets}
        onChange={(media) => update({ ...feature, media })}
      />
      <TextField
        label="Label"
        value={feature.label}
        onChange={(label) => update({ ...feature, label })}
      />
      <CompactRichTextField
        label="Statement"
        value={feature.statementBody ?? plainToRichText(feature.statement)}
        onChange={(statementBody) =>
          update({
            ...feature,
            statement: richTextToPlain(statementBody),
            statementBody,
          })
        }
      />
      <CompactRichTextField
        label="Supporting note"
        value={feature.noteBody ?? plainToRichText(feature.note ?? '')}
        onChange={(noteBody) =>
          update({
            ...feature,
            note: richTextToPlain(noteBody) || undefined,
            noteBody,
          })
        }
      />
    </div>
  );
}

function PricingExtrasFields({
  block,
  onChange,
}: {
  block: PricingBlock;
  onChange(block: PricingBlock): void;
}) {
  const items = block.payload.extras ?? [];
  const update = (extras: typeof items) =>
    onChange({
      ...block,
      payload: { ...block.payload, ownershipVersion: 1, extras },
    });
  return (
    <div className="home-fields">
      <TextField
        label="Section heading"
        value={block.payload.extrasHeading ?? ''}
        onChange={(extrasHeading) =>
          onChange({
            ...block,
            payload: {
              ...block.payload,
              ownershipVersion: 1,
              extrasHeading: extrasHeading || undefined,
            },
          })
        }
      />
      <div className="home-process-list">
        {items.map((item, index) => (
          <article className="home-process-item" key={item.key}>
            <CollectionHeader
              label={`Extra ${index + 1}`}
              index={index}
              count={items.length}
              onMove={(direction) => update(moveItem(items, index, direction))}
              onRemove={() => update(items.filter((_, current) => current !== index))}
            />
            <div className="form-grid form-grid--two">
              <TextField
                label="Name"
                value={item.label}
                onChange={(label) => update(replaceAt(items, index, { ...item, label }))}
              />
              <TextField
                label="Price"
                value={item.priceLabel}
                onChange={(priceLabel) => update(replaceAt(items, index, { ...item, priceLabel }))}
              />
            </div>
          </article>
        ))}
      </div>
      <Button
        variant="secondary"
        leadingIcon={Plus}
        onClick={() =>
          update([...items, { key: crypto.randomUUID(), label: 'New extra', priceLabel: '0 €' }])
        }
      >
        Add extra
      </Button>
    </div>
  );
}

function PricingGiftFields({
  block,
  onChange,
}: {
  block: PricingBlock;
  onChange(block: PricingBlock): void;
}) {
  const gift = block.payload.gift ?? {
    heading: 'Gift a photoshoot',
    body: plainToRichText('Gift cards are available for any package.'),
    actionLabel: 'Get a gift card',
  };
  const update = (next: typeof gift) =>
    onChange({
      ...block,
      payload: { ...block.payload, ownershipVersion: 1, gift: next },
    });
  return (
    <div className="home-fields">
      <div className="form-grid form-grid--two">
        <TextField
          label="Heading"
          value={gift.heading}
          onChange={(heading) => update({ ...gift, heading })}
        />
        <TextField
          label="Action label"
          value={gift.actionLabel}
          onChange={(actionLabel) => update({ ...gift, actionLabel })}
        />
      </div>
      <CompactRichTextField
        label="Text"
        value={gift.body}
        onChange={(body) => update({ ...gift, body })}
      />
    </div>
  );
}

function FaqFields({
  block,
  onChange,
  headingOnly = false,
}: {
  block: FaqBlock;
  onChange(block: FaqBlock): void;
  headingOnly?: boolean;
}) {
  const payload = block.payload;
  const items =
    payload.source === 'Inline'
      ? payload.items
      : [
          {
            key: 'pricing-faq-1',
            question: 'New question',
            answer: plainToRichText('Add the answer.'),
          },
        ];
  const updatePayload = (patch: Partial<typeof payload>) =>
    onChange({ ...block, payload: { ...payload, ...patch } as FaqBlock['payload'] });
  const updateItems = (next: typeof items) =>
    onChange({
      ...block,
      payload: {
        source: 'Inline',
        eyebrow: payload.eyebrow,
        heading: payload.heading,
        accent: payload.accent,
        items: next,
      },
    });
  return (
    <div className="home-fields">
      <EditorFieldGroup title="Section text">
        <div className="form-grid form-grid--two">
          {!headingOnly && (
            <TextField
              label="Section label"
              value={payload.eyebrow ?? ''}
              onChange={(eyebrow) => updatePayload({ eyebrow: eyebrow || undefined })}
            />
          )}
          <TextField
            label="Section heading"
            value={payload.heading ?? ''}
            onChange={(heading) => updatePayload({ heading: heading || undefined })}
          />
          {!headingOnly && (
            <TextField
              label="Italic text"
              value={payload.accent ?? ''}
              optional
              onChange={(accent) => updatePayload({ accent: accent || undefined })}
            />
          )}
        </div>
      </EditorFieldGroup>
      <EditorFieldGroup title="Questions">
        <div className="home-process-list">
          {items.map((item, index) => (
            <EditorCollectionItem
              key={item.key}
              title={item.question}
              summary={richTextToPlain(item.answer)}
              label={`Question ${index + 1}`}
              index={index}
              count={items.length}
              onMove={(direction) => updateItems(moveItem(items, index, direction))}
              onRemove={() => updateItems(items.filter((_, current) => current !== index))}
            >
              <TextField
                label="Question"
                value={item.question}
                onChange={(question) => updateItems(replaceAt(items, index, { ...item, question }))}
              />
              <CompactRichTextField
                label="Answer"
                value={item.answer}
                onChange={(answer) => updateItems(replaceAt(items, index, { ...item, answer }))}
              />
            </EditorCollectionItem>
          ))}
        </div>
        <Button
          variant="secondary"
          leadingIcon={Plus}
          onClick={() =>
            updateItems([
              ...items,
              {
                key: crypto.randomUUID(),
                question: 'New question',
                answer: plainToRichText('Add the answer.'),
              },
            ])
          }
        >
          Add question
        </Button>
      </EditorFieldGroup>
    </div>
  );
}

function ProcessFields({
  block,
  pricing,
  showEditorialFields = false,
  showActionLabel = true,
  onChange,
}: {
  block: ProcessBlock;
  pricing?: VersionedPricing;
  showEditorialFields?: boolean;
  showActionLabel?: boolean;
  onChange(block: ProcessBlock): void;
}) {
  const payload = block.payload;
  const [additionalOpen, setAdditionalOpen] = useState(Boolean(payload.eyebrow));
  const catalogItems =
    pricing?.steps.map((step, index) => ({
      key: step.payload.key,
      eyebrow: String(index + 1).padStart(2, '0'),
      title: step.payload.title,
      body: step.payload.body,
    })) ?? [];
  const items = payload.source === 'Inline' ? payload.items : catalogItems;
  const updateItems = (next: typeof items) =>
    onChange({
      ...block,
      payload: {
        ...payload,
        source: 'Inline',
        items: next.map((item, index) => ({
          ...item,
          eyebrow: String(index + 1).padStart(2, '0'),
        })),
      },
    });
  return (
    <div className="home-fields">
      <EditorFieldGroup title="Section text">
        {payload.source === 'Pricing' ? (
          <p className="inline-note">
            This page currently follows the shared Pricing process. The first edit creates a
            page-specific copy.
          </p>
        ) : null}
        {showEditorialFields ? (
          <>
            <div className="form-grid form-grid--two">
              <TextField
                label="Section heading"
                value={payload.heading ?? ''}
                onChange={(heading) =>
                  onChange({ ...block, payload: { ...payload, heading: heading || undefined } })
                }
              />
              <TextField
                label="Italic text"
                value={payload.accent ?? ''}
                optional
                onChange={(accent) =>
                  onChange({ ...block, payload: { ...payload, accent: accent || undefined } })
                }
              />
              {showActionLabel && (
                <TextField
                  label="Action label"
                  value={payload.actionLabel ?? ''}
                  onChange={(actionLabel) =>
                    onChange({
                      ...block,
                      payload: { ...payload, actionLabel: actionLabel || undefined },
                    })
                  }
                />
              )}
            </div>
            <details
              className="page-editor-additional"
              open={additionalOpen}
              onToggle={(event) => setAdditionalOpen(event.currentTarget.open)}
            >
              <summary>Additional settings</summary>
              <TextField
                label="Section label"
                value={payload.eyebrow ?? ''}
                onChange={(eyebrow) =>
                  onChange({ ...block, payload: { ...payload, eyebrow: eyebrow || undefined } })
                }
              />
            </details>
          </>
        ) : (
          <TextField
            label="Section heading"
            value={payload.heading ?? ''}
            onChange={(heading) =>
              onChange({ ...block, payload: { ...payload, heading: heading || undefined } })
            }
          />
        )}
      </EditorFieldGroup>
      <EditorFieldGroup title="Steps">
        <div className="home-process-list">
          {items.map((item, index) => (
            <EditorCollectionItem
              key={item.key}
              title={item.title}
              summary={richTextToPlain(item.body)}
              label={`Step ${String(index + 1).padStart(2, '0')}`}
              index={index}
              count={items.length}
              onMove={(direction) => updateItems(moveItem(items, index, direction))}
              onRemove={() => updateItems(items.filter((_, current) => current !== index))}
            >
              <div className="page-step-fields">
                <TextField
                  label="Step title"
                  value={item.title}
                  onChange={(title) => updateItems(replaceAt(items, index, { ...item, title }))}
                />
                <CompactRichTextField
                  label="Step text"
                  value={item.body}
                  onChange={(body) => updateItems(replaceAt(items, index, { ...item, body }))}
                />
              </div>
            </EditorCollectionItem>
          ))}
        </div>
        <Button
          variant="secondary"
          leadingIcon={Plus}
          disabled={items.length >= 6}
          onClick={() =>
            updateItems([
              ...items,
              {
                key: crypto.randomUUID(),
                title: 'New step',
                body: plainToRichText('Describe this stage.'),
              },
            ])
          }
        >
          Add step
        </Button>
      </EditorFieldGroup>
    </div>
  );
}

function ProcessDetailsFields({
  block,
  onChange,
}: {
  block: ProcessBlock;
  onChange(block: ProcessBlock): void;
}) {
  const details = block.payload.details ?? [];
  const update = (next: typeof details) =>
    onChange({ ...block, payload: { ...block.payload, details: next } });
  return (
    <div className="home-fields">
      {details.map((item, index) => (
        <article className="home-process-item" key={item.key}>
          <CollectionHeader
            label={`Detail ${index + 1}`}
            index={index}
            count={details.length}
            onMove={(direction) => update(moveItem(details, index, direction))}
            onRemove={() => update(details.filter((_, current) => current !== index))}
          />
          <div className="form-grid form-grid--three">
            <TextField
              label="Label"
              value={item.label}
              onChange={(label) => update(replaceAt(details, index, { ...item, label }))}
            />
            <TextField
              label="Text"
              value={item.text}
              onChange={(text) => update(replaceAt(details, index, { ...item, text }))}
            />
            <TextField
              label="Italic text"
              value={item.accent ?? ''}
              optional
              onChange={(accent) =>
                update(
                  replaceAt(details, index, {
                    ...item,
                    accent: accent || undefined,
                  }),
                )
              }
            />
          </div>
        </article>
      ))}
    </div>
  );
}

function ServiceGalleryFields({
  block,
  assets,
  onChange,
}: {
  block: EditorialBlock;
  assets: readonly AdminAsset[];
  onChange(block: EditorialBlock): void;
}) {
  const gallery = block.payload.gallery;
  if (!gallery) return <MissingSection />;
  const update = (next: typeof gallery) =>
    onChange({ ...block, payload: { ...block.payload, gallery: next } });
  return (
    <div className="home-fields">
      <div className="form-grid form-grid--two">
        <TextField
          label="Section label"
          value={gallery.eyebrow ?? ''}
          optional
          onChange={(eyebrow) => update({ ...gallery, eyebrow: eyebrow || undefined })}
        />
        <TextField
          label="Heading before accent"
          value={gallery.heading ?? ''}
          optional
          onChange={(heading) => update({ ...gallery, heading: heading || undefined })}
        />
        <TextField
          label="Italic text"
          value={gallery.accent ?? ''}
          optional
          onChange={(accent) => update({ ...gallery, accent: accent || undefined })}
        />
      </div>
      <CompactRichTextField
        label="Supporting text"
        value={gallery.body ?? plainToRichText('')}
        onChange={(body) => update({ ...gallery, body })}
      />
      <ContextualGalleryField
        title="Wedding gallery"
        profile="standard"
        description="Upload photographs, choose from Media and arrange their public order."
        items={gallery.items}
        assets={assets}
        createItem={(media) => ({ key: crypto.randomUUID(), media })}
        onChange={(items) => update({ ...gallery, items })}
      />
      <div className="home-process-list">
        {gallery.items.map((item, index) => (
          <article className="home-process-item" key={item.key ?? item.media.assetId}>
            <p className="eyebrow">Image {index + 1} caption</p>
            <TextField
              label="Caption"
              value={item.caption ?? ''}
              optional
              onChange={(caption) =>
                update({
                  ...gallery,
                  items: replaceAt(gallery.items, index, {
                    ...item,
                    caption: caption || undefined,
                  }),
                })
              }
            />
          </article>
        ))}
      </div>
      <TextField
        label="Portfolio link label"
        value={gallery.actionLabel ?? ''}
        optional
        onChange={(actionLabel) => update({ ...gallery, actionLabel: actionLabel || undefined })}
      />
    </div>
  );
}

function JourneyFields({
  block,
  onChange,
}: {
  block: ProcessBlock;
  onChange(block: ProcessBlock): void;
}) {
  const chapters = block.payload.chapters ?? [];
  const update = (next: typeof chapters) =>
    onChange({ ...block, payload: { ...block.payload, chapters: next } });
  return (
    <div className="home-fields">
      <div className="form-grid form-grid--three">
        <TextField
          label="Eyebrow"
          value={block.payload.eyebrow ?? ''}
          onChange={(eyebrow) =>
            onChange({
              ...block,
              payload: { ...block.payload, eyebrow: eyebrow || undefined },
            })
          }
        />
        <TextField
          label="Heading"
          value={block.payload.heading ?? ''}
          onChange={(heading) =>
            onChange({
              ...block,
              payload: { ...block.payload, heading: heading || undefined },
            })
          }
        />
        <TextField
          label="Italic text"
          value={block.payload.accent ?? ''}
          optional
          onChange={(accent) =>
            onChange({
              ...block,
              payload: { ...block.payload, accent: accent || undefined },
            })
          }
        />
      </div>
      {chapters.map((chapter, chapterIndex) => (
        <section className="home-linked" key={chapter.key}>
          <div className="home-fields">
            <TextField
              label={`Chapter ${chapterIndex + 1} label`}
              value={chapter.label}
              onChange={(label) => update(replaceAt(chapters, chapterIndex, { ...chapter, label }))}
            />
            <HeadingFields
              showAfter={false}
              value={chapter.heading}
              onChange={(heading) =>
                update(replaceAt(chapters, chapterIndex, { ...chapter, heading }))
              }
            />
            <CompactRichTextField
              label={`Chapter ${chapterIndex + 1} description`}
              value={{
                type: 'document',
                children: chapter.items.flatMap((item) => item.body.children),
              }}
              onChange={(body) =>
                update(
                  replaceAt(chapters, chapterIndex, {
                    ...chapter,
                    items: [
                      {
                        key: chapter.items[0]?.key ?? `${chapter.key}-description`,
                        title: [chapter.heading.before, chapter.heading.accent]
                          .filter(Boolean)
                          .join(' '),
                        body,
                      },
                    ],
                  }),
                )
              }
            />
          </div>
        </section>
      ))}
    </div>
  );
}

function ReviewFields({
  block,
  onChange,
}: {
  block: ProcessBlock;
  onChange(block: ProcessBlock): void;
}) {
  const reviews = block.payload.reviews ?? [];
  const update = (next: typeof reviews) =>
    onChange({ ...block, payload: { ...block.payload, reviews: next } });
  return (
    <div className="home-fields">
      {reviews.map((review, index) => (
        <article className="home-process-item" key={review.key}>
          <CollectionHeader
            label={`Review ${index + 1}`}
            index={index}
            count={reviews.length}
            onMove={(direction) => update(moveItem(reviews, index, direction))}
            onRemove={() => update(reviews.filter((_, current) => current !== index))}
          />
          <TextAreaField
            label="Quote"
            value={review.quote}
            onChange={(quote) => update(replaceAt(reviews, index, { ...review, quote }))}
          />
          <TextField
            label="Attribution"
            value={review.attribution}
            onChange={(attribution) =>
              update(replaceAt(reviews, index, { ...review, attribution }))
            }
          />
          <div className="home-choice">
            <Checkbox
              label="Approved for website"
              checked={review.approvedForWebsite === true}
              onChange={(event) =>
                update(
                  replaceAt(reviews, index, {
                    ...review,
                    approvedForWebsite: event.currentTarget.checked,
                  }),
                )
              }
            />
          </div>
        </article>
      ))}
      <Button
        variant="secondary"
        leadingIcon={Plus}
        disabled={reviews.length >= 8}
        onClick={() =>
          update([
            ...reviews,
            {
              key: crypto.randomUUID(),
              quote: 'Add the client quote.',
              attribution: 'Client names · Location, year',
            },
          ])
        }
      >
        Add review
      </Button>
    </div>
  );
}

function StatementFields({
  block,
  onChange,
}: {
  block: ProcessBlock;
  onChange(block: ProcessBlock): void;
}) {
  const statement = block.payload.statement;
  if (!statement) return <MissingSection />;
  return (
    <div className="home-fields">
      <HeadingFields
        value={statement}
        onChange={(next) =>
          onChange({
            ...block,
            payload: { ...block.payload, statement: { ...statement, ...next } },
          })
        }
        labels={['Text before accent', 'Italic text', 'Text after accent']}
      />
      <TextField
        label="Second accent"
        optional
        value={statement.secondAccent ?? ''}
        onChange={(secondAccent) =>
          onChange({
            ...block,
            payload: {
              ...block.payload,
              statement: { ...statement, secondAccent: secondAccent || undefined },
            },
          })
        }
      />
      <TextField
        label="Text after second accent"
        optional
        value={statement.afterSecond ?? ''}
        onChange={(afterSecond) =>
          onChange({
            ...block,
            payload: {
              ...block.payload,
              statement: { ...statement, afterSecond: afterSecond || undefined },
            },
          })
        }
      />
    </div>
  );
}

function CtaFields({ block, onChange }: { block: CtaBlock; onChange(block: CtaBlock): void }) {
  const payload = block.payload;
  const update = (patch: Partial<typeof payload>) =>
    onChange({ ...block, payload: { ...payload, ...patch } });
  return (
    <div className="home-fields">
      <EditorFieldGroup title="Message">
        <div className="form-grid form-grid--two">
          <TextField
            label="Heading before accent"
            value={payload.heading}
            onChange={(heading) => update({ heading })}
          />
          <TextField
            label="Italic text"
            value={payload.accent ?? ''}
            optional
            onChange={(accent) => update({ accent: accent || undefined })}
          />
        </div>
        <CompactRichTextField
          label="Supporting text"
          value={payload.body ?? plainToRichText('')}
          onChange={(body) => update({ body })}
        />
      </EditorFieldGroup>
      <EditorFieldGroup title="Action">
        <TextField
          label="Button label"
          value={payload.action.label}
          onChange={(label) => update({ action: { ...payload.action, label } })}
        />
        <p className="inline-note">
          Destination and button presentation are protected by the template.
        </p>
      </EditorFieldGroup>
    </div>
  );
}

function HeadingFields({
  value,
  onChange,
  showAfter = true,
  labels = ['Heading before accent', 'Italic text', 'Heading after accent'],
}: {
  value: { before: string; accent?: string; after?: string };
  onChange(value: { before: string; accent?: string; after?: string }): void;
  labels?: [string, string, string];
  showAfter?: boolean;
}) {
  return (
    <div className={`form-grid form-grid--${showAfter ? 'three' : 'two'}`}>
      <TextField
        label={labels[0]}
        value={value.before}
        onChange={(before) => onChange({ ...value, before })}
      />
      <TextField
        label={labels[1]}
        value={value.accent ?? ''}
        optional
        onChange={(accent) => onChange({ ...value, accent: accent || undefined })}
      />
      {showAfter ? (
        <TextField
          label={labels[2]}
          value={value.after ?? ''}
          optional
          onChange={(after) => onChange({ ...value, after: after || undefined })}
        />
      ) : null}
    </div>
  );
}

export function PageSeoFields({
  page,
  payload,
  siteAccess,
  assets,
  onChange,
}: {
  page: PageSummary;
  payload: VersionedPage['payload'];
  siteAccess: VersionedPage['siteAccess'];
  assets: readonly AdminAsset[];
  onChange(patch: PageDraftPatch): void;
}) {
  return (
    <div className="home-fields">
      <TextField
        label="SEO title"
        value={payload.seo.title}
        onChange={(title) => onChange({ payload: { ...payload, seo: { ...payload.seo, title } } })}
      />
      <TextAreaField
        label="SEO description"
        value={payload.seo.description}
        onChange={(description) =>
          onChange({ payload: { ...payload, seo: { ...payload.seo, description } } })
        }
      />
      <div className="form-grid form-grid--two">
        <SelectField
          label="Website access"
          value={siteAccess}
          options={['AdminOnly', 'Public']}
          optionLabels={{ AdminOnly: 'Admin only', Public: 'Public' }}
          onChange={(value) => onChange({ siteAccess: value as VersionedPage['siteAccess'] })}
        />
      </div>
      <div className="home-choice">
        <Checkbox
          label="Prevent search indexing"
          description="Keeps a public page on the website but removes it from search and the sitemap."
          checked={payload.seo.noindex}
          onChange={(event) =>
            onChange({
              payload: {
                ...payload,
                seo: { ...payload.seo, noindex: event.currentTarget.checked },
              },
            })
          }
        />
      </div>
      <ContextualMediaField
        title="Social sharing image"
        profile="wide"
        cropProfile="social"
        description="Optional override for Open Graph and social previews."
        value={payload.seo.socialImage}
        assets={assets}
        onChange={(socialImage) =>
          onChange({ payload: { ...payload, seo: { ...payload.seo, socialImage } } })
        }
      />
      <p className="inline-note">
        Canonical path is protected and follows <strong>{page.route}</strong>.
      </p>
    </div>
  );
}

function PackageSource({
  pricing,
  service,
}: {
  pricing?: VersionedPricing;
  service?: NonNullable<PricingPlanItem['payload']['service']>;
}) {
  const plans =
    pricing?.plans.filter(
      (plan) => !service || plan.payload.service === service || inferPlanService(plan) === service,
    ) ?? [];
  return (
    <div className="home-fields">
      {plans.length ? (
        <div className="home-choice-list">
          {plans.map((plan) => (
            <div className="home-choice" key={plan.id}>
              <span>
                <strong>{plan.payload.title}</strong>
                <small>{plan.payload.priceLabel}</small>
              </span>
              <span className="entity-row__kind">{plan.visibility}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="inline-note">No matching packages have been created yet.</p>
      )}
      <LinkedSection
        title="Packages have one source of truth"
        body="Prices, images and inclusions are edited in Packages and reused everywhere."
        href="/packages"
        action="Manage Packages"
      />
    </div>
  );
}

function JournalCategories({ journal }: { journal: readonly JournalPostSummary[] }) {
  const categories = [...new Set(journal.map((entry) => entry.payload.category))].sort();
  return categories.length ? (
    <div className="home-choice-list home-choice-list--compact">
      {categories.map((category) => (
        <div className="home-choice" key={category}>
          <strong>{category}</strong>
        </div>
      ))}
    </div>
  ) : (
    <p className="inline-note">
      Categories appear automatically after the first article is created.
    </p>
  );
}

function JournalFeatured({ journal }: { journal: readonly JournalPostSummary[] }) {
  const eligible = [...journal]
    .filter((entry) => entry.siteAccess === 'Public' && publicationTime(entry) <= Date.now())
    .sort((left, right) => publicationTime(right) - publicationTime(left));
  const featured = eligible[0];
  if (!featured) {
    const candidate = [...journal].sort(
      (left, right) => publicationTime(right) - publicationTime(left),
    )[0];
    return (
      <div className="home-linked">
        <div>
          <strong>No published Featured article yet</strong>
          <p>
            {candidate
              ? `${candidate.payload.title} will become eligible after it is public.`
              : 'Create the first Journal article to populate this section.'}
          </p>
        </div>
        <Link className="button button--quiet" to="/journal">
          Open Journal <ExternalLink size={14} aria-hidden="true" />
        </Link>
      </div>
    );
  }
  return (
    <div className="home-linked">
      <div>
        <strong>{featured.payload.title}</strong>
        <p>
          Automatically selected from {featured.payload.publishedAt ?? featured.payload.dateLabel}.
        </p>
      </div>
      <span className="entity-row__kind">
        {featured.siteAccess === 'Public' ? 'Public' : 'Admin only'}
      </span>
    </div>
  );
}

function LinkedSection({
  title,
  body,
  href,
  action,
}: {
  title: string;
  body: string;
  href: string;
  action: string;
}) {
  return (
    <div className="home-linked">
      <div>
        <strong>{title}</strong>
        <p>{body}</p>
      </div>
      <Link className="button button--quiet" to={href}>
        {action}
        <ExternalLink size={14} aria-hidden="true" />
      </Link>
    </div>
  );
}

function CollectionHeader({
  label,
  index,
  count,
  onMove,
  onRemove,
}: {
  label: string;
  index: number;
  count: number;
  onMove(direction: -1 | 1): void;
  onRemove(): void;
}) {
  return (
    <header>
      <span>{label}</span>
      <EditorCollectionActions index={index} count={count} onMove={onMove} onRemove={onRemove} />
    </header>
  );
}

function MissingSection({
  actionLabel,
  onAction,
}: {
  actionLabel?: string;
  onAction?: () => void;
} = {}) {
  return (
    <div className="home-fields">
      <p className="inline-note" role="status">
        This protected section is missing from the current page data.
      </p>
      {actionLabel && onAction ? (
        <Button variant="secondary" leadingIcon={Plus} onClick={onAction}>
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}

function blockOfType<Type extends AdminPageBlock['type']>(
  blocks: readonly AdminPageBlock[],
  type: Type,
): Extract<AdminPageBlock, { type: Type }> | undefined {
  return blocks.find(
    (block): block is Extract<AdminPageBlock, { type: Type }> => block.type === type,
  );
}

function replaceAt<Item>(items: readonly Item[], index: number, value: Item): Item[] {
  const next = [...items];
  next[index] = value;
  return next;
}

function moveItem<Item>(items: readonly Item[], index: number, direction: -1 | 1): Item[] {
  const target = index + direction;
  if (target < 0 || target >= items.length) return [...items];
  const next = [...items];
  [next[index], next[target]] = [next[target]!, next[index]!];
  return next;
}

function publicationTime(entry: JournalPostSummary) {
  const value = entry.payload.publishedAt ?? entry.payload.dateLabel;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function inferPlanService(plan: PricingPlanItem) {
  if (plan.payload.action.href === '/weddings') return 'Weddings';
  if (plan.payload.action.href === '/proposals') return 'Proposals';
  return /family|couple/i.test(plan.payload.title) ? 'Family' : 'Personal';
}
